import type * as React from "react";
import { cn } from "@/lib/utils";

interface FilterBarProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * Stack of labeled quick-filter rows shown above the data toolbar. Each child
 * should be a <FilterRow>; rows stack vertically so every filter (status,
 * capacity, …) sits on its own line and the controls don't run together.
 */
export function FilterBar({ children, className }: FilterBarProps) {
  return <div className={cn("space-y-1.5", className)}>{children}</div>;
}

interface FilterRowProps {
  label: string;
  children: React.ReactNode;
}

/**
 * One labeled filter line. The label sits in a narrow left-aligned column so
 * each row begins at the page's left edge (in line with the search bar and
 * table) rather than floating in a wide gutter; the controls wrap to the right
 * of it. On mobile the label stacks above the controls.
 */
export function FilterRow({ label, children }: FilterRowProps) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
      <span className="text-muted-foreground shrink-0 whitespace-nowrap text-xs font-medium sm:w-36 sm:pt-1">
        {label}
      </span>
      <div className="flex flex-1 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}
